import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { downloadJobICS } from '../lib/icsExport'
import {
  Calendar, MapPin, Clock, Users, ChevronDown, ChevronUp,
  Tent, AlertTriangle, CheckCircle,
} from 'lucide-react'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return null
  return new Date(d + 'T12:00').toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })
}

function fmtShortDate(d) {
  if (!d) return null
  return new Date(d + 'T12:00').toLocaleDateString('en-CA', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function daysUntil(dateStr) {
  if (!dateStr) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(dateStr + 'T00:00:00')
  return Math.round((target - today) / (1000 * 60 * 60 * 24))
}

function urgencyLabel(days) {
  if (days === 0) return { label: 'Today!', bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }
  if (days === 1) return { label: 'Tomorrow', bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }
  if (days <= 7)  return { label: `${days} days`, bg: '#DBEAFE', color: '#1D4ED8', border: '#93C5FD' }
  if (days <= 30) return { label: `${days} days`, bg: '#F3F4F6', color: '#374151', border: '#D1D5DB' }
  return { label: `${days} days`, bg: '#F3F4F6', color: '#9CA3AF', border: '#E5E7EB' }
}

const EVENT_TYPE_ICONS = {
  wedding: '💍',
  corporate: '🏢',
  birthday: '🎂',
  festival: '🎉',
  community: '🏘️',
  other: '📅',
}

const STATUS_LABELS = {
  confirmed: 'Confirmed', staffing: 'Staffing', crew_set: 'Crew Set',
  in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled',
}

const STATUS_COLORS = {
  confirmed:   { bg: '#DBEAFE', color: '#1D4ED8' },
  staffing:    { bg: '#EDE9FE', color: '#6D28D9' },
  crew_set:    { bg: '#CFFAFE', color: '#164E63' },
  in_progress: { bg: '#FEF3C7', color: '#92400E' },
  completed:   { bg: '#D1FAE5', color: '#065F46' },
  cancelled:   { bg: '#F3F4F6', color: '#6B7280' },
}

// ─── ICS download icon (calendar with down arrow, inline SVG workaround) ──────
function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <polyline points="8 14 12 18 16 14"/>
      <line x1="12" y1="12" x2="12" y2="18"/>
    </svg>
  )
}

// ─── Job Card ─────────────────────────────────────────────────────────────────

function JobCard({ assignment, isPast }) {
  const [expanded, setExpanded] = useState(false)
  const job = assignment.jobs

  if (!job) return null

  const days = daysUntil(job.setup_date || job.event_date)
  const urgency = !isPast && days !== null ? urgencyLabel(days) : null
  const eventIcon = EVENT_TYPE_ICONS[job.event_type] || '📅'
  const statusColor = STATUS_COLORS[job.status] || STATUS_COLORS.confirmed
  const totalHours = (parseFloat(job.estimated_setup_hours) || 0) + (parseFloat(job.estimated_teardown_hours) || 0)

  const eventDate    = fmtDate(job.event_date)
  const setupDate    = job.setup_date && job.setup_date !== job.event_date ? fmtShortDate(job.setup_date) : null
  const teardownDate = job.teardown_date && job.teardown_date !== job.event_date ? fmtShortDate(job.teardown_date) : null

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: isPast
        ? '0 1px 4px rgba(0,0,0,.06)'
        : '0 2px 12px rgba(15,31,61,.10)',
      border: `1px solid ${isPast ? '#E5E7EB' : '#CBD5E1'}`,
      opacity: isPast ? 0.75 : 1,
      marginBottom: 12,
      transition: 'box-shadow 0.15s',
    }}>
      {/* Top accent bar — green for upcoming, grey for past */}
      <div style={{
        height: 4,
        background: isPast
          ? '#E5E7EB'
          : days === 0 || days === 1
            ? '#F59E0B'
            : 'var(--color-green)',
      }} />

      {/* Main card content */}
      <div style={{ padding: '16px 18px' }}>
        {/* Row 1: date + urgency badge */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontSize: 20 }}>{eventIcon}</span>
              <span style={{
                fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--color-navy)',
              }}>
                {job.event_type
                  ? job.event_type.charAt(0).toUpperCase() + job.event_type.slice(1)
                  : 'Job'}
              </span>
              <span style={{
                fontSize: 'var(--text-xs)', fontWeight: 600,
                padding: '2px 8px', borderRadius: 10,
                background: statusColor.bg, color: statusColor.color,
              }}>
                {STATUS_LABELS[job.status] || job.status}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
              <Calendar size={13} style={{ flexShrink: 0 }} />
              <span>{eventDate || 'Date TBD'}</span>
            </div>
          </div>

          {/* Urgency pill (upcoming only) */}
          {urgency && (
            <div style={{
              flexShrink: 0,
              padding: '6px 12px', borderRadius: 20,
              background: urgency.bg, color: urgency.color,
              border: `1px solid ${urgency.border}`,
              fontSize: 'var(--text-xs)', fontWeight: 700,
              textAlign: 'center', whiteSpace: 'nowrap',
            }}>
              {urgency.label}
            </div>
          )}
        </div>

        {/* Location */}
        {job.address && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 6,
            marginTop: 10, fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)',
          }}>
            <MapPin size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{job.address}</span>
          </div>
        )}

        {/* Setup / teardown row */}
        {(setupDate || teardownDate || totalHours > 0) && (
          <div style={{
            display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap',
            fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
          }}>
            {setupDate && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Setup:</span> {setupDate}
              </span>
            )}
            {teardownDate && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontWeight: 600 }}>Teardown:</span> {teardownDate}
              </span>
            )}
            {totalHours > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Clock size={11} />
                ~{totalHours} hrs
              </span>
            )}
            {job.crew_required > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Users size={11} />
                {job.crew_required}-person crew
              </span>
            )}
          </div>
        )}

        {/* Job notes preview (first line) */}
        {job.job_notes && !expanded && (
          <div style={{
            marginTop: 10, padding: '8px 12px',
            background: 'var(--color-bg)', borderRadius: 6,
            fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)',
            borderLeft: '3px solid var(--color-green)',
            fontStyle: 'italic',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {job.job_notes}
          </div>
        )}

        {/* Expanded content */}
        {expanded && (
          <div style={{ marginTop: 12 }}>
            {job.job_notes && (
              <div style={{
                padding: '10px 14px',
                background: 'var(--color-bg)', borderRadius: 6,
                fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)',
                borderLeft: '3px solid var(--color-green)', lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
              }}>
                <div style={{ fontWeight: 600, color: 'var(--color-navy)', marginBottom: 4, fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  Job Notes
                </div>
                {job.job_notes}
              </div>
            )}

            {/* Inventory summary */}
            {job.job_inventory && job.job_inventory.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontWeight: 600, color: 'var(--color-navy)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 6 }}>
                  Equipment
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {job.job_inventory.map(ji => (
                    <div key={ji.id} style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: 'var(--text-sm)', padding: '5px 0',
                      borderBottom: '1px solid var(--color-border)',
                    }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        {ji.inventory_categories?.name || '—'}
                      </span>
                      <span style={{ fontWeight: 600, color: 'var(--color-navy)' }}>
                        × {ji.quantity_reserved}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-border)',
          gap: 8,
        }}>
          {/* Expand / collapse */}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setExpanded(e => !e)}
            style={{ fontSize: 'var(--text-xs)', gap: 4 }}
          >
            {expanded ? <><ChevronUp size={12} /> Less</> : <><ChevronDown size={12} /> Details</>}
          </button>

          {/* Add to Calendar */}
          <button
            className="btn btn-sm"
            onClick={() => downloadJobICS(job)}
            title="Download .ics calendar file"
            style={{
              fontSize: 'var(--text-xs)', gap: 4,
              background: 'var(--color-navy)', color: 'white', border: 'none',
            }}
          >
            <CalendarIcon /> Add to Calendar
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── MyJobsPage ───────────────────────────────────────────────────────────────

export default function MyJobsPage() {
  const { user } = useAuth()
  const [upcoming, setUpcoming] = useState([])
  const [past,     setPast]     = useState([])
  const [loading,  setLoading]  = useState(true)
  const [showPast, setShowPast] = useState(false)

  const load = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)

    const today = new Date().toISOString().slice(0, 10)

    // Fetch all confirmed assignments for this user, with full job + inventory detail
    const { data, error } = await supabase
      .from('job_staff')
      .select(`
        id, status, pinged_at, responded_at, confirmed_at,
        jobs (
          id, event_date, setup_date, teardown_date,
          event_type, address, status,
          estimated_setup_hours, estimated_teardown_hours,
          crew_required, job_notes,
          job_inventory (
            id, quantity_reserved,
            inventory_categories ( name, unit )
          )
        )
      `)
      .eq('user_id', user.id)
      .eq('status', 'confirmed')
      .order('created_at')

    if (!error && data) {
      // Sort by event_date
      const sorted = data
        .filter(a => a.jobs)
        .sort((a, b) => {
          const da = a.jobs.event_date || '9999'
          const db = b.jobs.event_date || '9999'
          return da.localeCompare(db)
        })

      setUpcoming(sorted.filter(a => (a.jobs.setup_date || a.jobs.event_date || '') >= today))
      setPast(sorted.filter(a => (a.jobs.setup_date || a.jobs.event_date || '') < today).reverse())
    }

    setLoading(false)
  }, [user?.id])

  useEffect(() => { load() }, [load])

  // ── Stats ─────────────────────────────────────────────────────────────────

  const thisMonthJobs = upcoming.filter(a => {
    if (!a.jobs?.event_date) return false
    const d = new Date(a.jobs.event_date + 'T12:00')
    const now = new Date()
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  }).length

  const nextJob = upcoming[0]?.jobs
  const daysToNext = nextJob ? daysUntil(nextJob.setup_date || nextJob.event_date) : null

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{
          fontSize: 'var(--text-xl)', fontWeight: 800,
          color: 'var(--color-navy)', margin: 0, lineHeight: 1.2,
        }}>
          My Jobs
        </h1>
        {user?.full_name && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
            Welcome back, {user.full_name.split(' ')[0]}
          </p>
        )}
      </div>

      {/* Stats row */}
      {!loading && (upcoming.length > 0 || past.length > 0) && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20,
        }}>
          {[
            {
              label: 'Next Job In',
              value: daysToNext === null ? '—' : daysToNext === 0 ? 'Today!' : daysToNext === 1 ? 'Tomorrow' : `${daysToNext}d`,
              highlight: daysToNext !== null && daysToNext <= 1,
            },
            { label: 'This Month', value: thisMonthJobs, highlight: false },
            { label: 'Upcoming',   value: upcoming.length, highlight: false },
          ].map(({ label, value, highlight }) => (
            <div key={label} style={{
              background: highlight ? 'var(--color-navy)' : 'white',
              borderRadius: 10, padding: '12px 14px', textAlign: 'center',
              border: `1px solid ${highlight ? 'transparent' : 'var(--color-border)'}`,
              boxShadow: highlight ? '0 2px 8px rgba(15,31,61,.15)' : 'none',
            }}>
              <div style={{
                fontSize: 'var(--text-2xl)', fontWeight: 800, lineHeight: 1,
                color: highlight ? 'white' : 'var(--color-navy)',
              }}>
                {value}
              </div>
              <div style={{
                fontSize: 'var(--text-xs)', color: highlight ? 'rgba(255,255,255,.65)' : 'var(--color-text-muted)',
                marginTop: 4,
              }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)',
        }}>
          Loading your jobs…
        </div>
      )}

      {/* Empty state */}
      {!loading && upcoming.length === 0 && past.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '60px 24px',
          background: 'white', borderRadius: 12,
          border: '1px solid var(--color-border)',
        }}>
          <Tent size={44} style={{ color: 'var(--color-text-muted)', marginBottom: 16 }} />
          <div style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--color-navy)', marginBottom: 8 }}>
            No jobs yet
          </div>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', maxWidth: 280, margin: '0 auto', lineHeight: 1.6 }}>
            When the office confirms you for an upcoming job, it'll appear here.
          </div>
        </div>
      )}

      {/* Upcoming jobs */}
      {!loading && upcoming.length > 0 && (
        <div>
          <div style={{
            fontSize: 'var(--text-xs)', fontWeight: 700,
            color: 'var(--color-text-muted)', textTransform: 'uppercase',
            letterSpacing: '.06em', marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <CheckCircle size={12} style={{ color: 'var(--color-green)' }} />
            Upcoming — {upcoming.length} job{upcoming.length !== 1 ? 's' : ''}
          </div>
          {upcoming.map(a => (
            <JobCard key={a.id} assignment={a} isPast={false} />
          ))}
        </div>
      )}

      {/* No upcoming but has past */}
      {!loading && upcoming.length === 0 && past.length > 0 && (
        <div style={{
          textAlign: 'center', padding: '32px 24px',
          background: 'white', borderRadius: 12,
          border: '1px solid var(--color-border)', marginBottom: 16,
        }}>
          <CheckCircle size={36} style={{ color: 'var(--color-green)', marginBottom: 12 }} />
          <div style={{ fontWeight: 700, fontSize: 'var(--text-md)', color: 'var(--color-navy)', marginBottom: 6 }}>
            All caught up!
          </div>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
            No upcoming jobs. Check back soon — the office will let you know when you're needed.
          </div>
        </div>
      )}

      {/* Past jobs (collapsible) */}
      {!loading && past.length > 0 && (
        <div style={{ marginTop: upcoming.length > 0 ? 24 : 0 }}>
          <button
            onClick={() => setShowPast(p => !p)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              background: 'none', border: 'none', padding: '8px 0',
              cursor: 'pointer', fontFamily: 'var(--font-body)',
              color: 'var(--color-text-muted)', textAlign: 'left',
            }}
          >
            <span style={{
              fontSize: 'var(--text-xs)', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.06em',
            }}>
              Past — {past.length} job{past.length !== 1 ? 's' : ''}
            </span>
            {showPast ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          {showPast && (
            <div style={{ marginTop: 8 }}>
              {past.map(a => (
                <JobCard key={a.id} assignment={a} isPast={true} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom info note */}
      {!loading && (upcoming.length > 0 || past.length > 0) && (
        <div className="alert alert-info" style={{ marginTop: 20, fontSize: 'var(--text-xs)' }}>
          <AlertTriangle size={13} style={{ flexShrink: 0 }} />
          Questions about a job? Contact the office at{' '}
          <a href="mailto:info@winnipegtentrentals.ca" style={{ color: 'var(--color-navy)', fontWeight: 600 }}>
            info@winnipegtentrentals.ca
          </a>
        </div>
      )}
    </div>
  )
}
